import { html } from '@nrk/dh-server-libs/html'
import Scrollhistorie from '@nrk/dh-basis-scrollhistorie'
import { closest } from '@nrk/dh-basis-common'
import { balanceAndWatch } from '../lib/balance-text-wrapper.js'
import debounce from 'debounce'
import { getArticleInfo } from './article-info.ts'

/* styles */
import styles from './introscroll.module.css'
import backgroundStyles from './background.module.css'
import cardStyles from './cards/cards.module.css'

const s = { ...styles, ...backgroundStyles, ...cardStyles }

export default class Introscroll extends Scrollhistorie {
  static get observedAttributes() {
    return ['background-color', ...super.observedAttributes]
  }

  #updating = false
  #viewport = { height: 0, width: 0 }
  #currentPercentage
  #debouncedUpdateSizes

  connectedCallback() {
    super.connectedCallback()
    this.#updateSizes()
    this.#debouncedUpdateSizes = debounce(this.#updateSizes, 500)
    window.addEventListener('resize', this.#debouncedUpdateSizes)
    // eslint-disable-next-line compat/compat
    if (document?.fonts?.ready?.then) {
      // eslint-disable-next-line promise/catch-or-return
      document.fonts.ready.then(this.#updateTitle)
    }

    this.#updateByline()

    // Date in byline is calculated on the client. Update byline to include new date.
    setTimeout(() => this.#updateByline(), 100)
    setTimeout(() => this.#updateByline(), 5000)

    /*
    Make media elements focusable so that we can adjust the scroll
    when a background image is focused (i.e. with a screen reader)
    */
    for (const elm of this.querySelectorAll('img, video, [role=img]')) {
      elm.tabIndex = '-1'
    }
    this.addEventListener('focus', this.#handleFocus, true)

    /*
    Polyfill text-wrap: balance.
    TODO: remove once Safari fully supports text-wrap: balance
    (There's currently a bug in Safari that disables text-wrap balance when used with box-decoration)
    https://bugs.webkit.org/show_bug.cgi?id=274145
    */
    if (!CSS.supports('text-wrap', 'balance') || navigator?.userAgent?.includes?.('Safari')) {
      requestAnimationFrame(() => {
        balanceAndWatch(this.querySelectorAll('.balance-text'))
      })
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this.removeEventListener('focus', this.#handleFocus, true)
    window.removeEventListener('resize', this.#debouncedUpdateSizes)
  }

  attributeChangedCallback(name, prevValue, newValue) {
    super.attributeChangedCallback(name, prevValue, newValue)

    if (name === 'background-color' && typeof newValue === 'string') {
      this.style.setProperty('--introscroll-background-color', newValue)
    }
  }

  /**
   * When a background image is focused:
   * - Adjust the scroll position to ensure that the background is visible.
   */
  #handleFocus = (e) => {
    const backgroundElement = e.target && closest(e.target, '[data-background-id]')
    if (!backgroundElement) {
      return
    }

    const backgroundId = backgroundElement.getAttribute('data-background-id')
    if (this.activeBackgroundId !== backgroundId) {
      const triggerElement = this.triggerElements.find(
        (elm) => elm.getAttribute('data-show-background') === backgroundId,
      )
      if (triggerElement) {
        window.scrollTo(0, triggerElement.offsetTop - window.innerHeight / 2)
      }
    }
  }

  #updateByline() {
    const { authors, publishedStrings } = getArticleInfo()

    const container = this.querySelector(`.${s.byline}`)
    const expandable = authors.length > 3

    const authorHTML = authors.map((author) => {
      return html`
        <div class="${s.author}">
          <a class="${s.author__name}" href="${author.href}" itemprop="author">${author.name}</a
          >&nbsp;
          <span class="${s.author__role}">${author.role}</span>
        </div>
      `
    })
    const expandHTML = html`
      <a id="intro-authors--expand" href="#intro-authors--expand">
        <span class="nrk-sr">Vis ${authors.length - 1} bidragsytere til</span>
        <span aria-hidden="true">+${authors.length - 1} til</span>
      </a>
    `
    authorHTML.splice(1, 0, expandHTML)

    container.innerHTML = html`
      <div class="${s.authors} ${expandable ? s.expandable : ''}">${authorHTML.join('')}</div>
      ${publishedStrings?.map((str) => `<div class="${s.publish__date}">${str}</div>`).join('') ??
      ''}
    `
  }

  #updateSizes = () => {
    if (this.#viewport.height !== document.documentElement.clientHeight) {
      this.#viewport.height = document.documentElement.clientHeight
    }

    if (this.#viewport.width !== document.documentElement.clientWidth) {
      this.#viewport.width = document.documentElement.clientWidth
      this.#updateTitle()
    }
  }

  #updateTitle = () => {
    const titleCardContainer = this.querySelector(`.${s.titleCard} .${s.textContainer}`)
    let fontReduce = 0
    this.style.setProperty('--introscroll-font-reduce', `${fontReduce}px`)
    while (titleCardContainer.scrollWidth > titleCardContainer.offsetWidth && fontReduce < 20) {
      fontReduce++
      this.style.setProperty('--introscroll-font-reduce', `${fontReduce}px`)
    }
  }

  get backgroundColor() {
    this.getAttribute('background-color')
  }

  set backgroundColor(val) {
    this.setAttribute('background-color', val)
  }
}
